'use client';
import { FC, useCallback, useRef } from 'react';
import VideoCarousel from 'components/VideoCarousel/VideoCarousel';
import { useYoutubeShorts } from 'hooks/useYoutubeShorts';

// Client-rendered so the YouTube fetch stays off the homepage's critical path.
// While loading we reserve height with a placeholder so the content below
// (More News, etc.) doesn't shift when the row fills in. If there are no shorts
// the section renders nothing.
const Shorts: FC = () => {
  const { videos, isLoading } = useYoutubeShorts();
  const sectionRef = useRef<HTMLElement>(null);

  // The prev/next arrows live in the heading row (outside the carousel's
  // clipped band), so they scroll the carousel's scroll container directly --
  // the same mechanism the shared Slider arrows use -- rather than relying on
  // the library's own in-band arrow buttons.
  const scroll = useCallback((direction: 1 | -1) => {
    const container = sectionRef.current?.querySelector<HTMLElement>(
      '.react-horizontal-scrolling-menu--scroll-container'
    );
    if (!container) return;
    container.scrollBy({
      left: direction * container.clientWidth,
      behavior: 'smooth',
    });
  }, []);

  if (isLoading) {
    return (
      <section
        className="home-shorts home-segment home-shorts-placeholder"
        aria-hidden="true"
      />
    );
  }

  if (!videos.length) return null;

  return (
    <section className="home-shorts home-segment" ref={sectionRef}>
      <div className="home-shorts-header">
        <h2 className="home-shorts-heading">Watch</h2>
        <div className="home-shorts-arrows">
          <button
            type="button"
            className="home-scroll-arrow home-scroll-arrow-left"
            aria-label="Scroll videos left"
            onClick={() => scroll(-1)}
          />
          <button
            type="button"
            className="home-scroll-arrow home-scroll-arrow-right"
            aria-label="Scroll videos right"
            onClick={() => scroll(1)}
          />
        </div>
      </div>
      <div className="home-shorts-inner">
        <VideoCarousel videos={videos} />
      </div>
    </section>
  );
};

export default Shorts;
